import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ChevronRight, Plus } from "lucide-react";
import {
  EntityPopover,
  EntityPopoverItem,
} from "../../../components/ui/popover/EntityPopover";

interface InvestigationEntityCardProps {
  title: string;
  items: string[];

  onAdd: () => void;
  onManage: () => void;

  disableAdd?: boolean;
  disableAddMessage?: string;
  disableManage?: boolean;

  popoverProps: {
    title?: string;
    icon?: React.ReactNode;
    items: string[] | EntityPopoverItem[];
    allowCopy?: boolean;
  };

  emptyState?: {
    icon: React.ReactNode;
    message: string;
  };
}

const pillClassName =
  "min-w-5 max-w-[180px] rounded-[8px] bg-[#F3F4F6] px-2 py-[2px] text-xs font-medium text-[#4B5563] truncate";

export const InvestigationEntityCard: React.FC<
  InvestigationEntityCardProps
> = ({
  title,
  items,
  onAdd,
  onManage,
  disableAdd = false,
  disableAddMessage,
  disableManage = false,
  popoverProps,
  emptyState,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<any>(null);
  const prevWidthRef = useRef<number>(0);

  const [visibleItems, setVisibleItems] = useState<string[]>(items);
  const [hiddenItems, setHiddenItems] = useState<string[]>([]);

  const isEmpty = items.length === 0;

  const calculateVisibleItems = useCallback(() => {
    const container = containerRef.current;
    if (!container || isEmpty) return;

    const clone = container.cloneNode(false) as HTMLDivElement;

    Object.assign(clone.style, {
      position: "absolute",
      visibility: "hidden",
      top: "-9999px",
      left: "-9999px",
      width: `${container.clientWidth}px`,
      height: "auto",
    });

    document.body.appendChild(clone);

    try {
      items.forEach((item) => {
        const div = document.createElement("div");
        div.className = `${pillClassName} inline-block`;
        div.textContent = item;
        clone.appendChild(div);
      });

      const chips = Array.from(clone.children) as HTMLElement[];
      if (!chips.length) {
        setVisibleItems([]);
        setHiddenItems([]);
        return;
      }

      const firstRowTop = chips[0].offsetTop;
      let secondRowTop: number | null = null;
      let cutoffIndex = chips.length;

      for (let i = 0; i < chips.length; i++) {
        const top = chips[i].offsetTop;

        if (top !== firstRowTop && secondRowTop === null) {
          secondRowTop = top;
        }

        if (secondRowTop !== null && top > secondRowTop) {
          cutoffIndex = i;
          break;
        }
      }

      if (cutoffIndex === chips.length) {
        setVisibleItems(items);
        setHiddenItems([]);
        return;
      }

      const hiddenItemsPill = document.createElement("div");
      hiddenItemsPill.className = `${pillClassName} inline-block`;
      clone.appendChild(hiddenItemsPill);

      const maxAllowedTop = secondRowTop ?? firstRowTop;

      while (cutoffIndex > 0) {
        const remainingCount = items.length - cutoffIndex;
        hiddenItemsPill.textContent = `+${remainingCount} more`;

        for (let i = cutoffIndex; i < chips.length; i++) {
          chips[i].style.display = "none";
        }

        const hiddenItemsPillTop = hiddenItemsPill.offsetTop;
        if (hiddenItemsPillTop <= maxAllowedTop) {
          break;
        }

        chips[cutoffIndex].style.display = "";
        cutoffIndex--;
      }

      setVisibleItems(items.slice(0, cutoffIndex));
      setHiddenItems(items.slice(cutoffIndex));
    } finally {
      clone.remove();
    }
  }, [items, isEmpty]);

  useLayoutEffect(() => {
    calculateVisibleItems();
  }, [items, isEmpty, calculateVisibleItems]);

  useEffect(() => {
    if (isEmpty) return;

    let timeoutId: NodeJS.Timeout;

    const observer = new ResizeObserver((entries) => {
      if (entries.length === 0) return;

      const width = Math.round(entries[0].contentRect.width);

      if (width > 0 && width !== prevWidthRef.current) {
        prevWidthRef.current = width;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(calculateVisibleItems, 10);
      }
    });

    if (containerRef.current) {
      prevWidthRef.current = Math.round(
        containerRef.current.getBoundingClientRect().width
      );
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [items, isEmpty, calculateVisibleItems]);

  return (
    <div className="flex h-full flex-col rounded-[8px] border border-[#E5E5E5] shadow-[0_1px_2px_rgba(0,0,0,0.05)] bg-white p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#000]">
            {title}
          </span>
          {!isEmpty && (
            <div
              className="flex min-w-[20px] items-center justify-center rounded-[8px] bg-[#EBEEFE] px-2 py-[2px]"
            >
              <span
                className="text-xs font-semibold leading-4 text-[#3551F3]"
              >
                {items.length}
              </span>
            </div>
          )}
        </div>

        {disableAdd && disableAddMessage ? (
          <div className="group relative">
            <button
              type="button"
              onClick={onAdd}
              disabled
              className="flex items-center justify-center rounded-[8px] bg-[#F5F5F5] px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus size={16} />
            </button>

            <div className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden w-max max-w-[240px] rounded-[6px] bg-[#1F2937] px-3 py-2 text-xs text-white shadow-lg group-hover:block">
              {disableAddMessage}
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onAdd}
            disabled={disableAdd}
            className="flex items-center justify-center rounded-[8px] bg-[#F5F5F5] px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus size={16} />
          </button>
        )}
      </div>

      {isEmpty ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E5E5E5] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
            {emptyState?.icon}
          </div>

          <p className="text-xs font-normal leading-4 text-[#737373]">
            {emptyState?.message ?? "No data available"}
          </p>
        </div>
      ) : (
        <>
          <div
            ref={containerRef}
            className="mt-3 flex flex-wrap gap-2"
          >
            {visibleItems.map((item, index) => (
              <div
                key={`${item}-${index}`}
                data-chip
                className={pillClassName}
                title={item}
              >
                {item}
              </div>
            ))}

            {hiddenItems.length > 0 && (
              <div
                className="relative"
                onMouseEnter={() => popoverRef.current?.open?.()}
                onMouseLeave={() => popoverRef.current?.close?.()}
              >
                <div
                  className={`${pillClassName} cursor-pointer`}
                >
                  +{hiddenItems.length} more
                </div>

                <EntityPopover
                  ref={popoverRef}
                  {...popoverProps}
                />
              </div>
            )}
          </div>

          <div className="flex-1" />

          <div className="mt-3 border-t border-[#E5E5E5]" />

          <div className="flex justify-end pt-3">
            <button
              type="button"
              onClick={onManage}
              disabled={disableManage}
              className="flex items-center gap-0.5 text-xs font-medium text-[#3551F3] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Manage
              <ChevronRight size={16} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};